import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, AppBar, Toolbar, IconButton, Typography, List, ListItem, Badge } from '@mui/material';
import { Menu, Close } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ProfileDropdown from 'src/components/ProfileDropdown';
import Logo from 'src/components/Logo';
import { useRouteAccess } from 'src/hooks/useRouteAccess';

const MobileNavBar = () => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const unreadCount = useSelector((state) => state.unread);
  const activeStatus = useSelector((state) => state.navbar?.activeStatus);
  const { filteredRoutes } = useRouteAccess();

  const isActive = (path, status) => {
    if (activeStatus) {
      return Array.isArray(status) ? status.includes(activeStatus) : status === activeStatus;
    }
    return location.pathname.includes(path);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const renderIcon = (Icon, path) => {
    if (path === '/chat' && unreadCount > 0) {
      return (
        <Badge badgeContent={unreadCount} color="primary">
          <Icon />
        </Badge>
      );
    }
    return <Icon />;
  };

  const drawerVariants = {
    open: {
      y: '0vh',
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 300,
        duration: 0.3,
      },
    },
    closed: {
      y: 'calc(100% - 90px)',
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  const listVariants = {
    open: {
      opacity: 1,
      height: '70svh',
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.05,
      },
    },
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        when: 'afterChildren',
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  const itemVariants = {
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.2 },
    },
    closed: {
      opacity: 0,
      y: 20,
      transition: { duration: 0.2 },
    },
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
        }}
      >
        <Toolbar>
          <Logo />
          <Typography style={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ProfileDropdown />
          </Box>
        </Toolbar>
      </AppBar>

      <motion.div
        initial="closed"
        animate={drawerOpen ? 'open' : 'closed'}
        variants={drawerVariants}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #ddd',
          zIndex: 1000,
          boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)',
          // overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '90px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderBottom: '1px solid #ddd',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              position: 'relative',
              width: '100%',
            }}
          >
            {filteredRoutes.topRoutes.slice(0, 2).map(({ path, label, Icon, status }) => (
              <Box
                key={path}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <IconButton
                  onClick={() => handleNavigation(path)}
                  sx={{
                    color: isActive(path, status) ? 'primary.main' : 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(28, 124, 226, 0.08)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '28px',
                    },
                  }}
                >
                  {renderIcon(Icon, path)}
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    color: isActive(path, status) ? 'primary.main' : 'text.secondary',
                    fontSize: '10px',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <IconButton
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer}
                sx={{
                  width: '56px',
                  height: '56px',
                  background: '#fff',
                  border: '1px solid',
                  borderRadius: '50%',
                  '&:hover, &:active, &:focus': {
                    background: '#fff',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '28px',
                  },
                }}
              >
                {drawerOpen ? <Close /> : <Menu />}
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '10px',
                }}
              >
                Menu
              </Typography>
            </Box>

            {filteredRoutes.topRoutes.slice(2, 4).map(({ path, label, Icon, status }) => (
              <Box
                key={path}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <IconButton
                  onClick={() => handleNavigation(path)}
                  sx={{
                    color: isActive(path, status) ? 'primary.main' : 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(28, 124, 226, 0.08)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '28px',
                    },
                  }}
                >
                  {renderIcon(Icon, path)}
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    color: isActive(path, status) ? 'primary.main' : 'text.secondary',
                    fontSize: '10px',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <AnimatePresence>
          <motion.div
            key="drawer-content"
            variants={listVariants}
            initial="closed"
            animate={drawerOpen ? 'open' : 'closed'}
            style={{ overflow: 'auto' }}
          >
            <List sx={{ p: 2 }}>
              {filteredRoutes.topRoutes.slice(4).map(({ path, label, Icon, status }) => (
                <motion.div key={path} variants={itemVariants}>
                  <ListItem
                    onClick={() => handleNavigation(path)}
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      width: 'auto',
                      maxWidth: '600px',
                      p: 2,
                      margin: '8px auto',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: isActive(path, status) ? 'rgba(28, 124, 226, 0.08)' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(28, 124, 226, 0.12)',
                      },
                    }}
                  >
                    {renderIcon(Icon, path)}
                    <Typography sx={{ ml: 2 }}>{label}</Typography>
                  </ListItem>
                </motion.div>
              ))}

              {filteredRoutes.bottomRoutes.map(({ path, label, Icon, status }) => (
                <motion.div key={path} variants={itemVariants}>
                  <ListItem
                    onClick={() => handleNavigation(path)}
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      width: 'auto',
                      maxWidth: '600px',
                      p: 2,
                      margin: '8px auto',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: isActive(path, status) ? 'rgba(28, 124, 226, 0.08)' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(28, 124, 226, 0.12)',
                      },
                    }}
                  >
                    {renderIcon(Icon, path)}
                    <Typography sx={{ ml: 2 }}>{label}</Typography>
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default MobileNavBar;
